import { useTheme } from '@brunommsilva/react-foundations';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LightModeIcon from '@mui/icons-material/LightMode';
import PersonIcon from '@mui/icons-material/Person';
import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import * as PATH from '../../config/paths';
import { Logout } from '../icons';

/** The avatar of the user, with the menu to change the theme and to log out. */
const UserMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      {/* User Avatar Menu Trigger */}
      <Box
        onClick={handleOpenMenu}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          p: 0.5,
          borderRadius: 2,
          '&:active': {
            bgcolor: 'action.hover',
            transform: 'scale(0.95)',
          },
          transition: 'all 0.1s ease',
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.main',
            boxShadow: '0 2px 8px rgba(13, 148, 136, 0.2)',
          }}
        >
          <PersonIcon sx={{ fontSize: 18 }} />
        </Avatar>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 18,
            color: 'text.secondary',
            transform: isMenuOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s ease',
          }}
        />
      </Box>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        onClick={handleCloseMenu}
        slotProps={{
          paper: {
            elevation: 4,
            sx: {
              mt: 1.5,
              minWidth: 220,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Theme Switcher */}
        <MenuItem sx={{ py: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
            {darkMode ? <DarkModeIcon /> : <LightModeIcon />}
          </ListItemIcon>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <ListItemText
              primary={
                darkMode
                  ? t('THEME_DARK') || 'Dark Mode'
                  : t('THEME_LIGHT') || 'Light Mode'
              }
              slotProps={{
                primary: {
                  sx: {
                    fontWeight: 600,
                    fontSize: '14px',
                    color: 'text.primary',
                  },
                },
              }}
            />
            <Switch
              size="small"
              checked={darkMode}
              onChange={(e) => {
                e.stopPropagation();
                toggleDarkMode();
              }}
              color="default"
            />
          </Box>
        </MenuItem>

        <Divider sx={{ opacity: 0.5 }} />

        {/* Logout */}
        <MenuItem
          onClick={() => navigate(PATH.LOGOUT_PATH)}
          sx={{ py: 1.5, color: 'error.main' }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
            <Logout sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText
            primary={t('LOGOUT')}
            slotProps={{
              primary: {
                sx: { fontWeight: 700, fontSize: '14px' },
              },
            }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
